import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Fab from "@material-ui/core/Fab";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PeopleIcon from "@material-ui/icons/People";
import DownloadIcon from "@material-ui/icons/GetApp";
import AddIcon from "@material-ui/icons/Add";
import ListAltIcon from "@material-ui/icons/ListAlt";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListDialog from "../../components/ContactListDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import planilhaExemplo from "../../assets/planilha.xlsx";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTLISTS") {
    const contactLists = action.payload;
    const newContactLists = [];
    contactLists.forEach((contactList) => {
      const idx = state.findIndex((u) => u.id === contactList.id);
      if (idx !== -1) state[idx] = contactList;
      else newContactLists.push(contactList);
    });
    return [...state, ...newContactLists];
  }
  if (action.type === "UPDATE_CONTACTLIST") {
    const contactList = action.payload;
    const idx = state.findIndex((u) => u.id === contactList.id);
    if (idx !== -1) { state[idx] = contactList; return [...state]; }
    return [contactList, ...state];
  }
  if (action.type === "DELETE_CONTACTLIST") {
    const idx = state.findIndex((u) => u.id === action.payload);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  mobileList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  cardIcon: {
    width: 42, height: 42, borderRadius: 10,
    background: "rgba(91,45,142,0.1)",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, color: "#5B2D8E",
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardName: { fontWeight: 700, fontSize: "0.95rem", color: theme.palette.text.primary },
  cardMeta: { fontSize: "0.78rem", color: theme.palette.text.secondary, marginTop: 2 },
  cardActions: { display: "flex", flexShrink: 0, gap: 2 },
  mobileHeader: {
    display: "flex", flexDirection: "column",
    gap: theme.spacing(1), width: "100%", padding: theme.spacing(0, 0, 1),
  },
  mobileHeaderRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  fab: { position: "fixed", bottom: theme.spacing(3), right: theme.spacing(3), zIndex: 10 },
  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary, gap: theme.spacing(1),
  },
}));

const ContactLists = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedContactList, setSelectedContactList] = useState(null);
  const [deletingContactList, setDeletingContactList] = useState(null);
  const [contactListModalOpen, setContactListModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [contactLists, dispatch] = useReducer(reducer, []);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contact-lists/", { params: { searchParam, pageNumber } });
        dispatch({ type: "LOAD_CONTACTLISTS", payload: data.records });
        setHasMore(data.hasMore);
        setLoading(false);
      } catch (err) { toastError(err); }
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const onEvent = (data) => {
      if (data.action === "update" || data.action === "create")
        dispatch({ type: "UPDATE_CONTACTLIST", payload: data.record });
      if (data.action === "delete")
        dispatch({ type: "DELETE_CONTACTLIST", payload: +data.id });
    };
    socket.on(`company-${companyId}-ContactList`, onEvent);
    return () => socket.off(`company-${companyId}-ContactList`, onEvent);
  }, []);

  const handleOpenModal = () => { setSelectedContactList(null); setContactListModalOpen(true); };
  const handleCloseModal = () => { setSelectedContactList(null); setContactListModalOpen(false); };
  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());
  const handleEdit = (cl) => { setSelectedContactList(cl); setContactListModalOpen(true); };
  const handleDelete = async (id) => {
    try {
      await api.delete(`/contact-lists/${id}`);
      toast.success(i18n.t("contactLists.toasts.deleted"));
    } catch (err) { toastError(err); }
    setDeletingContactList(null);
    setSearchParam(""); setPageNumber(1);
  };
  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const renderMobileCard = (cl) => (
    <div key={cl.id} className={classes.card}>
      <div className={classes.cardIcon}><ListAltIcon fontSize="small" /></div>
      <div className={classes.cardBody}>
        <div className={classes.cardName}>{cl.name}</div>
        <div className={classes.cardMeta}>
          <PeopleIcon style={{ fontSize: 13, verticalAlign: "middle", marginRight: 3 }} />
          {cl.contactsCount || 0} contactos
        </div>
      </div>
      <div className={classes.cardActions}>
        <a href={planilhaExemplo} download="planilha.xlsx">
          <IconButton size="small"><DownloadIcon fontSize="small" /></IconButton>
        </a>
        <IconButton size="small" onClick={() => history.push(`/contact-lists/${cl.id}/contacts`)}>
          <PeopleIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => handleEdit(cl)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingContactList(cl); }}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </div>
    </div>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingContactList && `${i18n.t("contactLists.confirmationModal.deleteTitle")} ${deletingContactList.name}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deletingContactList.id)}
      >
        {i18n.t("contactLists.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ContactListDialog
        open={contactListModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="form-dialog-title"
        contactListId={selectedContactList && selectedContactList.id}
      />

      {isMobile ? (
        <>
          <MainHeader>
            <div className={classes.mobileHeader}>
              <div className={classes.mobileHeaderRow}>
                <Title>{i18n.t("contactLists.title")}</Title>
                <Typography variant="caption" color="textSecondary">{contactLists.length} listas</Typography>
              </div>
              <TextField
                fullWidth size="small" variant="outlined"
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search" value={searchParam} onChange={handleSearch}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: "gray" }} fontSize="small" /></InputAdornment> }}
              />
            </div>
          </MainHeader>
          <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
            {contactLists.length === 0 && !loading ? (
              <div className={classes.emptyState}>
                <ListAltIcon style={{ fontSize: 48, opacity: 0.3 }} />
                <Typography variant="body2">Sin listas de contactos</Typography>
              </div>
            ) : (
              <div className={classes.mobileList}>
                {contactLists.map(renderMobileCard)}
                {loading && <Typography variant="caption" align="center" color="textSecondary">Cargando...</Typography>}
              </div>
            )}
          </Paper>
          <Fab color="primary" className={classes.fab} onClick={handleOpenModal}><AddIcon /></Fab>
        </>
      ) : (
        <>
          <MainHeader>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 16 }}>
              <Title>{i18n.t("contactLists.title")}</Title>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <TextField
                  placeholder={i18n.t("contacts.searchPlaceholder")}
                  type="search" value={searchParam} onChange={handleSearch}
                  size="small" variant="outlined"
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: "gray" }} /></InputAdornment> }}
                />
                <Button variant="contained" color="primary" onClick={handleOpenModal}>
                  {i18n.t("contactLists.buttons.add")}
                </Button>
              </div>
            </div>
          </MainHeader>
          <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{i18n.t("contactLists.table.name")}</TableCell>
                  <TableCell align="center">{i18n.t("contactLists.table.contacts")}</TableCell>
                  <TableCell align="center">{i18n.t("contactLists.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contactLists.map((cl) => (
                  <TableRow key={cl.id}>
                    <TableCell align="center">{cl.name}</TableCell>
                    <TableCell align="center">{cl.contactsCount || 0}</TableCell>
                    <TableCell align="center">
                      <a href={planilhaExemplo} download="planilha.xlsx">
                        <IconButton size="small"><DownloadIcon /></IconButton>
                      </a>
                      <IconButton size="small" onClick={() => history.push(`/contact-lists/${cl.id}/contacts`)}><PeopleIcon /></IconButton>
                      <IconButton size="small" onClick={() => handleEdit(cl)}><EditIcon /></IconButton>
                      <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingContactList(cl); }}><DeleteOutlineIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={3} />}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default ContactLists;
